"use client";

import { useState } from "react";
import { useSearchParams } from "next/navigation";

const TYPE_LABEL: Record<string, string> = {
  sample: "Sample request",
  bulk: "Bulk quote request",
  "landed-cost": "Landed cost quote request",
};

// Bulk orders only — the business does not ship single units. These are the
// order sizes buyers actually pick from; anything else goes in the message.
const QUANTITY_OPTIONS = [30, 50, 100, 300, 500, 1000];

export default function ContactForm() {
  const searchParams = useSearchParams();
  const product = searchParams.get("product");
  const variant = searchParams.get("variant");
  const type = searchParams.get("type");
  const destination = searchParams.get("destination");

  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("sending");
    setError(null);
    const form = new FormData(e.currentTarget);
    const body = {
      name: String(form.get("name") || ""),
      email: String(form.get("email") || ""),
      phone: String(form.get("phone") || "") || null,
      message: String(form.get("message") || ""),
      quantity: String(form.get("quantity") || "") || null,
      destination: String(form.get("destination") || "") || null,
      budgetRange: String(form.get("budgetRange") || "") || null,
      productSlug: product,
    };
    try {
      const res = await fetch("/api/enquiries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Something went wrong. Please try again.");
      setStatus("sent");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Something went wrong.");
    }
  }

  if (status === "sent") {
    return (
      <div className="rounded-lg p-6" style={{ background: "var(--good-soft)", border: "1px solid var(--good)" }}>
        <div className="font-semibold mb-1" style={{ color: "var(--good)" }}>
          Thanks — we got it.
        </div>
        <p className="text-sm" style={{ color: "var(--ink-muted)" }}>
          One of our team will review your message and get back to you directly.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {(product || type) && (
        <div className="text-xs rounded-md px-3 py-2" style={{ background: "var(--accent-soft)", color: "var(--accent-strong)" }}>
          {type ? TYPE_LABEL[type] ?? type : "Enquiry"}
          {product ? ` — ${product}${variant ? ` (${variant})` : ""}` : ""}
        </div>
      )}

      <label className="flex flex-col gap-1 text-sm">
        <span style={{ color: "var(--ink-faint)" }}>Name *</span>
        <input name="name" required className="rounded-md px-3 py-2" style={inputStyle} />
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: "var(--ink-faint)" }}>Email *</span>
          <input name="email" type="email" required className="rounded-md px-3 py-2" style={inputStyle} />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: "var(--ink-faint)" }}>Phone / WhatsApp</span>
          <input name="phone" className="rounded-md px-3 py-2" style={inputStyle} />
        </label>
      </div>

      <label className="flex flex-col gap-1 text-sm">
        <span style={{ color: "var(--ink-faint)" }}>What do you need? *</span>
        <textarea name="message" required rows={4} className="rounded-md px-3 py-2" style={inputStyle} />
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: "var(--ink-faint)" }}>Quantity</span>
          <select name="quantity" defaultValue="" className="rounded-md px-3 py-2" style={inputStyle}>
            <option value="" disabled>
              Select quantity
            </option>
            {QUANTITY_OPTIONS.map((q) => (
              <option key={q} value={q}>
                {q} pieces
              </option>
            ))}
            <option value="Other">Other (describe below)</option>
          </select>
          <span className="text-xs" style={{ color: "var(--ink-faint)" }}>
            Minimum order: 10 pieces
          </span>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: "var(--ink-faint)" }}>Destination</span>
          <input name="destination" defaultValue={destination ?? ""} placeholder="e.g. Jakarta" className="rounded-md px-3 py-2" style={inputStyle} />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span style={{ color: "var(--ink-faint)" }}>Budget range</span>
          <input name="budgetRange" placeholder="optional" className="rounded-md px-3 py-2" style={inputStyle} />
        </label>
      </div>

      {error && (
        <div className="text-sm" style={{ color: "var(--warn)" }}>
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={status === "sending"}
        className="rounded-lg px-5 py-2.5 text-sm font-semibold self-start"
        style={{ background: "var(--accent)", color: "#fff", opacity: status === "sending" ? 0.7 : 1 }}
      >
        {status === "sending" ? "Sending..." : "Send"}
      </button>
    </form>
  );
}

const inputStyle: React.CSSProperties = {
  border: "1px solid var(--line-strong)",
  background: "var(--surface)",
  color: "var(--ink)",
};
